import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import parse from "html-react-parser";
import type { Product } from "@/interfaces/product";
import { displayProductName } from "@/utils/product-helper";

function CheckmarkCircle() {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        bgcolor: "var(--color-dark-dust)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <CheckIcon sx={{ color: "var(--color-white)", fontSize: 16 }} />
    </Box>
  );
}

interface ImpactPanelProps {
  product: Product;
  /** Image above the checklist instead of beside it (recipient flow on tablets). */
  stacked?: boolean;
  /** Checklist labels wrap instead of ellipsizing (recipient sheet on phones). */
  wrapLabels?: boolean;
}

export default function ImpactPanel({
  product,
  stacked = false,
  wrapLabels = false,
}: ImpactPanelProps) {
  const impactImage = product.impactImage ?? product.imageUrl;
  const checkmarks =
    product.esgLabels && product.esgLabels.length > 0
      ? product.esgLabels
      : (product.impactCheckmarks ?? product.impactTiles.map((t) => t.label));
  const impactDescription =
    product.impactDescription ??
    `Your purchase of ${displayProductName(product.name)} contributes directly to ${product.impactTag}, making a real difference in people's lives.`;

  return (
    <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {/* Two-column: circular image (left) + checkmarks (right) */}
        <Box
          sx={{
            display: "flex",
            gap: "24px",
            alignItems: "flex-start",
            ...(stacked ? { flexDirection: "column" } : {}),
          }}
        >
          {/* Circular image with ring */}
          <Box
            component="img"
            src={impactImage}
            alt={`Impact image for ${product.name}`}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://placehold.co/160x160?text=Impact";
            }}
            sx={{
              width: 160,
              height: 160,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              boxShadow: `0 0 0 2px var(--color-white),
                          0 0 0 4px var(--color-smoke)`,
            }}
          />

          {/* Checkmarks column — responsive gap based on item count */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: checkmarks.length <= 3 ? "center" : "space-between",
              gap: checkmarks.length <= 3 ? "24px" : checkmarks.length === 4 ? "12px" : "8px",
              minHeight: 160,
              ...(stacked ? { minHeight: 0, alignSelf: "stretch", flex: "none" } : {}),
            }}
          >
            {checkmarks.map((item) => (
              <Box key={item} sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <CheckmarkCircle />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "var(--color-rodeo-black)",
                    ...(wrapLabels
                      ? {}
                      : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Impact paragraph */}
        <Box
          sx={{
            fontSize: "14px",
            lineHeight: "24px",
            color: "var(--color-rodeo-black)",
            "& p": { m: 0, mb: 1.5 },
            "& p:last-child": { mb: 0 },
            "& ul, & ol": { pl: 2.5, m: 0, mb: 1 },
            "& li": { mb: 0.5 },
            "& a": { color: "var(--color-primary-active)" },
          }}
        >
          {parse(impactDescription)}
        </Box>
      </Box>
    </Box>
  );
}
